/** @jsxImportSource @emotion/react */
import { Header, TruncatedString } from '@quickbit/backoffice-core-frontend';
import {
  Typography,
  useTheme,
  DateInput,
  Table,
  Icon,
  Pagination,
  UtilityButton,
  ToggleButtonGroup,
  Option,
  Snackbar,
} from '@quickbit/qb-design-system';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsync, useAsyncFn } from 'react-use';
import { MerchantSelect, QBLoader } from 'components';
import {
  createSettlementReport,
  exportDAC8Report,
  getAccountingOrders,
} from './api';
import { AmountDisplay, ExportCsv } from './components';
import { SortOptions } from './types';

const formatEurAmount = (number: number) => `€ ${number}`;
const PAGE_SIZE = 16;

const CELL_KEY_TO_SORT_FIELD: Record<string, SortOptions['field']> = {
  completedAt: 'COMPLETED_AT',
  orderId: 'ORDER_ID',
  settlementAmount: 'SETTLEMENT_AMOUNT',
  processingFee: 'PROCESSING_FEE',
  purchasedAt: 'PURCHASED_AT',
};

export const Accounting = () => {
  const navigate = useNavigate();
  const { spacing, palette } = useTheme();
  const [dateFilterRange, setDateFilterRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [page, setPage] = useState(1);
  const [sortOptions, setSortOptions] = useState<SortOptions>();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [selectedMerchant, setSelectedMerchant] = useState<Option | null>(null);
  const [showError, setShowError] = useState<boolean>(false);

  const [{ loading: downloadDac8Loading }, downloadDac8Report] =
    useAsyncFn(async () => {
      const csvResponse: Blob = await exportDAC8Report();

      const link = document.createElement('a');
      link.href = URL.createObjectURL(csvResponse);
      link.download = 'dac8-report.xml';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, []);

  const HEADERS = useMemo(
    () => [
      ...(isProcessing
        ? [{ label: 'Purchased At', cellKey: 'purchasedAt' }]
        : []),
      { label: 'Completed At', cellKey: 'completedAt' },
      { label: 'Order ID', cellKey: 'orderId' },
      { label: 'Amount', cellKey: 'settlementAmount' },
      { label: 'Fee', cellKey: 'processingFee' },
    ],
    [isProcessing]
  );

  const handleSort = useCallback(
    (cellKey: string, asc: boolean) => {
      setSortOptions({
        field: CELL_KEY_TO_SORT_FIELD[cellKey],
        ascending: asc,
      });
    },
    [setSortOptions]
  );

  const filters = useMemo(
    () =>
      selectedMerchant && dateFilterRange[0] && dateFilterRange[1]
        ? {
            fromTime: dateFilterRange[0],
            toTime: dateFilterRange[1],
            merchantId: selectedMerchant.value,
            isProcessing,
          }
        : null,
    [selectedMerchant, dateFilterRange, isProcessing]
  );

  const { value, loading } = useAsync(async () => {
    if (!filters) return null;
    return getAccountingOrders({
      pagination: { size: PAGE_SIZE, page },
      filters,
      sortOptions,
    });
  }, [page, selectedMerchant, sortOptions, filters]);

  const [{ loading: reportLoading, error: createError }, createReport] =
    useAsyncFn(async () => {
      if (!filters) return null;
      try {
        const report = await createSettlementReport({
          filters,
        });
        void navigate(`/accounting/reports/${report.uuid}`);
        return report;
      } catch (error) {
        setShowError(true);
        throw error;
      }
    }, [selectedMerchant, filters]);

  const handleResetFilters = () => {
    setDateFilterRange([null, null]);
    setSelectedMerchant(null);
    setIsProcessing(false);
  };

  const rows = useMemo(
    () =>
      value?.orders.map((order) => ({
        value: {
          completedAt: order.completedAt?.toLocaleString(),
          purchasedAt: order.purchasedAt?.toLocaleString(),
          orderId: (
            <div>
              <TruncatedString text={order.uuid} />
              {order.referenceId && (
                <Typography variant="bodySmallest">
                  Reference ID: {order.referenceId}
                </Typography>
              )}
            </div>
          ),
          settlementAmount: order.settlement?.euroAmount,
          processingFee: order.settlement?.euroFee,
        },
      })),
    [value]
  );

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateRows: 'auto auto auto 1fr auto',
        height: '100%',
      }}
    >
      <Header>
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div css={{ display: 'flex', gap: spacing.xxs }}>
            <Typography variant="subtitle">Accounting</Typography>
          </div>
          <div
            css={{
              display: 'grid',
              gap: spacing.xxs,
              gridTemplateColumns: 'auto auto 1fr',
            }}
          >
            <UtilityButton
              text="Export DAC 8 report"
              onClick={() => void downloadDac8Report()}
              disabled={downloadDac8Loading}
              loading={downloadDac8Loading}
            />
            <UtilityButton
              text="Generate report"
              onClick={() => void createReport()}
              disabled={reportLoading || !filters}
              loading={reportLoading}
            />
            {filters && (
              <ExportCsv
                filters={filters}
                sortOptions={sortOptions}
                disabled={!selectedMerchant}
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                csvFileName={selectedMerchant?.label + '_orders' || 'orders'}
              />
            )}
          </div>
        </div>
      </Header>
      <div
        css={{
          display: 'flex',
          gap: spacing.xxxs,
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <MerchantSelect
          merchant={selectedMerchant}
          setMerchant={(data) => {
            setPage(1);
            setSelectedMerchant(data as Option);
          }}
        />
        <DateInput
          placeholder="All time"
          type="range"
          value={dateFilterRange}
          onChange={(e: [Date | null, Date | null]) => {
            setPage(1);
            setDateFilterRange(e);
          }}
          withTime
        />
        <div>
          <ToggleButtonGroup
            toggleOptions={[
              { label: 'Processing', value: 'PROCESSING' },
              { label: 'Others', value: '' },
            ]}
            onChange={(option) => setIsProcessing(option === 'PROCESSING')}
            selected={isProcessing ? 'PROCESSING' : ''}
          />
        </div>
        <UtilityButton
          variant="text"
          text="Reset filters"
          onClick={handleResetFilters}
        />
        {loading && (
          <Icon
            name="spinner"
            className={{ svg: { color: palette.grey.default } }}
          />
        )}
      </div>
      {value && (
        <>
          <div
            css={{
              marginTop: spacing.xs,
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: spacing.xxs,
            }}
          >
            <AmountDisplay
              title="Total Sales Volume"
              icon="EUR"
              amount={formatEurAmount(value.aggregatedData.totalAmount)}
            />
            <AmountDisplay
              title="Net Settlement"
              icon="EUR"
              amount={formatEurAmount(value.aggregatedData.settlementAmount)}
            />
            <AmountDisplay
              title="Transactions"
              icon="plus"
              amount={`${value.aggregatedData.orderCount}`}
            />
            <AmountDisplay
              title="Processing Fees"
              icon="EUR"
              amount={formatEurAmount(value.aggregatedData.processingFee)}
            />
          </div>
          <div css={{ marginTop: spacing.xxs }}>
            <Table headers={HEADERS} rows={rows ?? []} onSort={handleSort} />
          </div>
          <div css={{ marginTop: spacing.xxs }}>
            <Pagination
              currentPage={page}
              onChange={setPage}
              numberOfElements={value.aggregatedData.orderCount}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}
      {(!value || !filters) && (
        <div
          css={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: spacing.m,
          }}
        >
          <Typography variant="heading2">Select a merchant and time</Typography>
        </div>
      )}
      {loading && <QBLoader />}
      <Snackbar
        variant="danger"
        message={[
          `Failed to create report. ${createError?.errorMessage}`,
          ...(createError?.errorDetails?.reason ?? []),
          'Please double check and try again.',
        ]}
        open={showError}
        onClose={() => setShowError(false)}
      />
    </div>
  );
};
